import React, { useState, useEffect, useCallback } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import DataTable, {
   DataTableBody,
   DataTableHeader,
   DataTableHead,
   DataTableCell,
   DataTableRow,
} from '@amzn/meridian/data-table';
import { getBatchEvents } from '../services/api';
import { BATCH_EVENT_TABLE } from '../appConfig';

interface MonitorItem {
   batch_id: string;
   process: string;
   user: string;
   ofd_date: string;
   horizon: string;
   round: string;
   inserted_at: string;
   initiate_status: string;
   utr_status: string;
   cycle_transfer_status: string;
   cap_release_status: string;
   cap_upload_status: string;
}

const OpsMonitor: React.FC = () => {
   const [amzlData, setAmzlData] = useState<MonitorItem[]>([]);
   const [ssdData, setSsdData] = useState<MonitorItem[]>([]);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);

   const loadData = useCallback(async () => {
       setIsLoading(true);
       setError(null);
       try {
           const [amzlResult, ssdResult] = await Promise.all([
               getBatchEvents(BATCH_EVENT_TABLE, 'AMZL'),
               getBatchEvents(BATCH_EVENT_TABLE, 'SSD'),
           ]);
           const sortFn = (a: any, b: any) =>
               new Date(b.inserted_at).getTime() - new Date(a.inserted_at).getTime();
           setAmzlData((Array.isArray(amzlResult) ? amzlResult : []).sort(sortFn).slice(0, 10));
           setSsdData((Array.isArray(ssdResult) ? ssdResult : []).sort(sortFn).slice(0, 10));
       } catch (err: any) {
           setError(err.message || 'Failed to load monitoring data');
       } finally {
           setIsLoading(false);
       }
   }, []);

   useEffect(() => { loadData(); }, [loadData]);

   const columns = [
       { id: 'batch_id', label: 'Batch ID' },
       { id: 'user', label: 'User' },
       { id: 'ofd_date', label: 'OFD Date' },
       { id: 'horizon', label: 'Horizon' },
       { id: 'round', label: 'Round' },
       { id: 'inserted_at', label: 'Created At' },
       { id: 'initiate_status', label: 'Initiate' },
       { id: 'utr_status', label: 'UTR' },
       { id: 'cap_release_status', label: 'Cap Release' },
       { id: 'cap_upload_status', label: 'Cap Upload' },
   ];

   const renderTable = (data: MonitorItem[], title: string) => (
       <Column spacing="300">
           <Heading level={5} type="h100">{title}</Heading>
            <DataTable
                size="small"
                showRowDividers={false}
                showColumnDividers={false}
                showStripes={true}
                showBorder={false}
            >
                <DataTableHead>
                   <DataTableRow highlightOnHover={false}>
                       {columns.map(col => (
                           <DataTableHeader key={col.id}>{col.label}</DataTableHeader>
                       ))}
                   </DataTableRow>
               </DataTableHead>
               <DataTableBody>
                   {data.map((item, idx) => (
                       <DataTableRow key={`${item.batch_id}-${idx}`} highlightOnHover={false}>
                           {columns.map(col => (
                               <DataTableCell key={col.id}>{(item as any)[col.id] || '-'}</DataTableCell>
                           ))}
                       </DataTableRow>
                   ))}
                   {data.length === 0 && (
                       <DataTableRow highlightOnHover={false}>
                           <DataTableCell colSpan={columns.length}>
                               <Row alignmentHorizontal="center" spacingInset="400">
                                   <Text color="secondary">No data</Text>
                               </Row>
                           </DataTableCell>
                       </DataTableRow>
                   )}
               </DataTableBody>
           </DataTable>
       </Column>
   );

   return (
       <Column spacing="400">
           <Heading level={4} type="h300">Ops Monitor</Heading>

           <Alert type="neutral" size="medium">
               Overview of recent batch executions across all programs.
           </Alert>

           <Row>
               <Button onClick={loadData} disabled={isLoading} size="small">
                   {isLoading ? 'Refreshing...' : 'Refresh'}
               </Button>
           </Row>

           {error && <Alert type="error" size="medium" onClose={() => setError(null)}>{error}</Alert>}

           {isLoading ? (
               <Row alignmentHorizontal="center" spacingInset="450"><Loader size="small" /></Row>
           ) : (
               <Column spacing="450">
                   {renderTable(amzlData, 'AMZL Recent Batches')}
                   {renderTable(ssdData, 'SSD Recent Batches')}
               </Column>
           )}
       </Column>
   );
};

export default OpsMonitor;
